'use strict';

import useModalProps from '~/hooks/useModalProps';
import useAuthModalState from '~/hooks/useAuthModalState';
import AuthSuccessView from '~/components/auth/AuthSuccessView';
import AuthCodeConfirm from '~/components/auth/AuthCodeConfirm';
import AuthSelectScenario from '~/components/auth/AuthSelectScenario';
import AuthLoginForm from '~/components/auth/AuthLoginForm';
import AuthRegisterForm from '~/components/auth/AuthRegisterForm';
import Loading from '~/components/Loading';

// Общий каркас модалки авторизации/регистрации. Вся бренд-специфика —
// пропсами (тексты, опции сценариев, флаги); состояние и API-логика —
// в useAuthModalState. Точки входа брендов (gj2/aviola) — тонкие обёртки.
function AuthModalShell(props) {
    const {
        selectOptions,
        selectSeparatorKey,
        selectLoginKey,
        loginPrimaryKey,
        loginSecondaryKey,
        loginSeparatorKey,
        registerSecondaryKey,
        registerSeparatorKey,
        requireMediaAgreement,
        agencyAgreementTextKey,
        firstNameFirst,
        successImageSrc,
        successDescriptionKey,
        successCloseKey,
        codePlaceholderKey,
        overlayLoading,
    } = props;

    const { mode: initialMode } = useModalProps('reactAuthModal', {}, { mode: 'login' }, {
        getPropsFromInstance: (instance) => ({
            mode: instance.$trigger && instance.$trigger.data('mode') || instance.current.mode || 'login',
        }),
    });

    const {
        mode, setMode,
        scenario,
        email, setEmail,
        lastname, setLastname,
        firstname, setFirstname,
        thirdname, setThirdname,
        company, setCompany,
        regNumber, setRegNumber,
        vatNumber, setVatNumber,
        address, setAddress,
        website, setWebsite,
        contact, setContact,
        agreeCb, setAgreeCb,
        agreeMedia, setAgreeMedia,
        phone, setPhone,
        phoneCode, setPhoneCode,
        phoneComplete, setPhoneComplete,
        code, handleChangeCode,
        status,
        name,
        timeout,
        isFetching,
        errors,
        resetForm,
        changeMode,
        handleLogin,
        handleSubmitRegister,
    } = useAuthModalState(initialMode);

    React.useEffect(() => {
        const handleClose = (e, instance) => {
            if (instance.current.src === '#reactAuthModal') {
                // Сбрасываем и поля, и шаг: mode через props не сбросится —
                // props.mode не меняется между открытиями, поэтому effect
                // на initialMode не срабатывает и success-шаг залипает.
                resetForm();
                setMode('login');
            }
        };
        $(document).on('afterClose.fb', handleClose);
        return () => {
            $(document).off('afterClose.fb', handleClose);
        };
    }, [resetForm, setMode]);

    if (overlayLoading && isFetching) {
        return <Loading active={true} />;
    }

    if (mode === 'success') {
        return (
            <AuthSuccessView
                scenario={scenario}
                name={name}
                imageSrc={successImageSrc}
                descriptionKey={successDescriptionKey}
                closeLabelKey={successCloseKey}
                isFetching={isFetching}
            />
        );
    }

    if (mode === 'code') {
        return (
            <AuthCodeConfirm
                phone={phone}
                code={code}
                onCodeChange={handleChangeCode}
                status={status}
                timeout={timeout}
                isFetching={isFetching}
                onResend={handleLogin}
                codePlaceholderKey={codePlaceholderKey}
            />
        );
    }

    if (mode === 'select') {
        return (
            <AuthSelectScenario
                options={selectOptions}
                separatorKey={selectSeparatorKey}
                loginLabelKey={selectLoginKey}
                onSelectOption={(option) => {
                    if (option.scenario) {
                        changeMode('register', option.scenario);
                    }
                    if (option.goal) {
                        ymReachGoal(option.goal);
                    }
                }}
                onGoLogin={() => changeMode('login')}
            />
        );
    }

    if (mode === 'register') {
        return (
            <AuthRegisterForm
                scenario={scenario}
                email={email} onEmailChange={setEmail}
                lastname={lastname} onLastnameChange={setLastname}
                firstname={firstname} onFirstnameChange={setFirstname}
                thirdname={thirdname} onThirdnameChange={setThirdname}
                company={company} onCompanyChange={setCompany}
                regNumber={regNumber} onRegNumberChange={setRegNumber}
                vatNumber={vatNumber} onVatNumberChange={setVatNumber}
                address={address} onAddressChange={setAddress}
                agreeCb={agreeCb} onAgreeChange={setAgreeCb}
                agreeMedia={agreeMedia} onAgreeMediaChange={setAgreeMedia}
                phone={phone} onPhoneCompleteChange={setPhoneComplete}
                onPhoneChange={(phoneValue, phoneCompleteValue) => {
                    setPhone(phoneValue);
                    if (phoneCompleteValue !== undefined) setPhoneComplete(phoneCompleteValue);
                }}
                phoneCode={phoneCode} onPhoneCodeChange={setPhoneCode}
                errors={errors}
                onSubmit={handleSubmitRegister}
                onGoLogin={() => changeMode('login')}
                secondaryKey={registerSecondaryKey}
                separatorKey={registerSeparatorKey}
                requireMediaAgreement={requireMediaAgreement}
                agencyAgreementTextKey={agencyAgreementTextKey}
                firstNameFirst={firstNameFirst}
            />
        );
    }

    if (mode === 'login') {
        return (
            <AuthLoginForm
                phone={phone} onPhoneChange={setPhone}
                phoneCode={phoneCode} onCodeChange={setPhoneCode}
                phoneComplete={phoneComplete} onCompleteChange={setPhoneComplete}
                errors={errors}
                onLogin={handleLogin}
                onGoSelect={() => changeMode('select')}
                primaryKey={loginPrimaryKey}
                secondaryKey={loginSecondaryKey}
                separatorKey={loginSeparatorKey}
            />
        );
    }

    return null;
}

export default AuthModalShell;
